import ReactGA from "react-ga4";

/**
 * Initialize Google Analytics 4
 * Call once in App initialization (usually in main.tsx or App.tsx)
 */
export const initializeAnalytics = () => {
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;

  if (!measurementId) {
    console.warn(
      "[Analytics] GA4_MEASUREMENT_ID not configured - analytics disabled",
    );
    return false;
  }

  try {
    ReactGA.initialize(measurementId);
    console.log("[Analytics] GA4 initialized with ID:", measurementId);
    return true;
  } catch (error) {
    console.error("[Analytics] Failed to initialize GA4:", error);
    return false;
  }
};

/**
 * Track page view
 */
export const trackPageView = (pathname: string) => {
  try {
    ReactGA.send({
      hitType: "pageview",
      page: pathname,
      title: document.title,
    });
  } catch (error) {
    console.error("[Analytics] Failed to track page view:", error);
  }
};

/**
 * Track custom event
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
) => {
  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
  } catch (error) {
    console.error("[Analytics] Failed to track event:", error);
  }
};

/**
 * Track user registration
 */
export const trackUserRegistration = (email: string) => {
  try {
    // Set user ID for cross-session tracking (anonymized)
    const userId = btoa(email).substring(0, 20); // Hash email to user ID
    ReactGA.set({ user_id: userId });

    trackEvent("Authentication", "user_registration", "visitor_signup");
  } catch (error) {
    console.error("[Analytics] Failed to track registration:", error);
  }
};

/**
 * Track POI/Attraction visit
 */
export const trackPoiVisit = (poiName: string) => {
  try {
    trackEvent("Attractions", "poi_visit", poiName, 1);
  } catch (error) {
    console.error("[Analytics] Failed to track POI visit:", error);
  }
};

/**
 * Track QR code scan
 */
export const trackQrScan = (qrCode: string) => {
  try {
    trackEvent("Interactions", "qr_scan", qrCode, 1);
  } catch (error) {
    console.error("[Analytics] Failed to track QR scan:", error);
  }
};

/**
 * Track NFC tag scan
 */
export const trackNfcScan = (nfcTag: string) => {
  try {
    trackEvent("Interactions", "nfc_scan", nfcTag, 1);
  } catch (error) {
    console.error("[Analytics] Failed to track NFC scan:", error);
  }
};

/**
 * Track dashboard view
 */
export const trackDashboardView = (viewName: string) => {
  try {
    trackPageView(`/dashboard/${viewName}`);
    trackEvent("Dashboard", "view_opened", viewName);
  } catch (error) {
    console.error("[Analytics] Failed to track dashboard view:", error);
  }
};

/**
 * Track report export
 */
export const trackReportExport = (format: string, reportType: string) => {
  try {
    trackEvent("Reports", "export_generated", `${reportType}_${format}`, 1);
  } catch (error) {
    console.error("[Analytics] Failed to track report export:", error);
  }
};

/**
 * Track consent acceptance
 */
export const trackConsentAcceptance = (consentType: string) => {
  try {
    trackEvent("Compliance", "consent_accepted", consentType, 1);
  } catch (error) {
    console.error("[Analytics] Failed to track consent acceptance:", error);
  }
};
